import React, { useState, useEffect, useRef } from "react";
import CircularTimer from "./CircularTimer";
import QUESTION_BANK from "./questions.json";

function getRandomQuestions(questionBank, numQuestions = 10) {
  const shuffled = questionBank
    .map((q) => ({ ...q, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .slice(0, numQuestions)
    .map(({ sort, ...q }) => q);
  return shuffled;
}

const INITIAL_TIMER = 60; // seconds per question

function App() {
  const [step, setStep] = useState("start"); // start | quiz | summary
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [current, setCurrent] = useState(0);
  const [timers, setTimers] = useState([]);
  const timerRef = useRef(null);
  const [feedback, setFeedback] = useState(Array(10).fill(null)); // null | "correct" | "wrong"

  // Start quiz
  const startQuiz = () => {
    const qs = getRandomQuestions(QUESTION_BANK, 10);
    setQuestions(qs);
    setAnswers(Array(qs.length).fill(null));
    setCurrent(0);
    setTimers(Array(qs.length).fill(INITIAL_TIMER));
    setFeedback(Array(qs.length).fill(null));
    setStep("quiz");
  };

  // Timer logic
  useEffect(() => {
    if (step !== "quiz") return;
    if (timers[current] === 0) return;
    timerRef.current = setInterval(() => {
      setTimers((prev) =>
        prev.map((t, i) => (i === current ? Math.max(t - 1, 0) : t))
      );
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [current, step, timers]);

  // Auto-move to next if timer hits 0
  useEffect(() => {
    if (step !== "quiz") return;
    if (timers[current] === 0 && current < questions.length - 1) {
      setCurrent((prev) => prev + 1);
    }
  }, [timers, current, step, questions.length]);

  const selectAnswer = (idx) => {
    if (timers[current] === 0) return;
    setAnswers((prev) => prev.map((a, i) => (i === current ? idx : a)));
    setFeedback((prev) =>
      prev.map((f, i) =>
        i === current
          ? idx === questions[current].correctOption
            ? "correct"
            : "wrong"
          : f
      )
    );
  };

  const nextQuestion = () => {
    if (current < questions.length - 1) setCurrent((c) => c + 1);
  };

  const prevQuestion = () => {
    if (current > 0) setCurrent((c) => c - 1);
  };

  const submitQuiz = () => setStep("summary");

  // Progress Dots
  const ProgressDots = () => (
    <div className="flex items-center gap-1 ml-3">
      {questions.map((_, idx) => (
        <span
          key={idx}
          className={`w-2.5 h-2.5 rounded-full inline-block transition
          ${idx < current
              ? "bg-blue-600"
              : idx === current
              ? "bg-blue-400"
              : "bg-gray-300"
          }`}
        />
      ))}
    </div>
  );

  // Results
  const getResults = () => {
    let correct = 0;
    let incorrect = 0;
    questions.forEach((q, i) => {
      if (answers[i] === null) return;
      if (answers[i] === q.correctOption) correct++;
      else incorrect++;
    });
    return { correct, incorrect };
  };

  // Restart
  const restart = () => setStep("start");

  return (
    <div className="min-h-screen bg-gradient-to-tr from-slate-100 to-blue-50 flex flex-col items-center justify-center p-2">
      <div className="shadow-2xl rounded-3xl bg-white p-6 w-full max-w-2xl transition">
        {/* Top Bar: Progress and Timer */}
        {(step === "quiz" || step === "summary") && (
          <div className="flex items-center justify-between mb-4">
            <div>
              <span className="font-semibold text-lg">
                Question {current + 1} of {questions.length}
              </span>
              <ProgressDots />
            </div>
            {step === "quiz" && (
              <CircularTimer value={timers[current]} max={INITIAL_TIMER} />
            )}
          </div>
        )}







        {/* Start Screen */}

        {step === "start" && (
  <div className="flex flex-col items-center gap-6">
    {/* CA Icon */}
    <img
      src="/ca-icon.png"
      alt="CA Icon"
      className="w-60 h-60 mb-2"
      style={{ objectFit: "contain" }}
    />
    <h1 className="text-2xl md:text-3xl font-extrabold text-center text-blue-900 mb-1">
      Analytical Skills Quiz
    </h1>
    <div className="text-lg text-blue-700 mb-2 font-semibold text-center">
      For Chartered Accountants
    </div>
    {/* Description/Instructions */}
    <div className="text-base text-gray-700 max-w-md text-center mb-3">
      <p>
        <strong>Welcome!</strong> This quiz is crafted to help Chartered Accountants assess and strengthen their analytical abilities—skills essential for modern accounting, audit, tax, and assurance scenarios.
      </p>
      <ul className="text-left mt-3 ml-4 list-disc text-sm text-gray-600">
        <li>10 Multiple-Choice Questions, 1 minute each</li>
        <li>Get immediate green/red feedback after every answer selection</li>
        <li>Review and change your answers anytime before submission</li>
        <li>See a clear summary of correct and incorrect answers at the end</li>
        <li>Designed for practical learning and self-assessment</li>
      </ul>
    </div>
    <button
      className="px-8 py-3 rounded-2xl bg-blue-700 text-white text-lg font-semibold hover:bg-blue-900 transition"
      onClick={startQuiz}
    >
      Start Quiz
    </button>
  </div>
)}



        






        {/* Quiz Screen */}
        {step === "quiz" && (
          <>
            <div className="mt-4 mb-4">
              <div className="text-xl font-semibold mb-6 text-center">
                {questions[current].question}
              </div>
              <div className="flex flex-col gap-4">
                {questions[current].options.map((opt, idx) => {
                  const isSelected = answers[current] === idx;
                  const correctIdx = questions[current].correctOption;
                  let btnClass =
                    "px-5 py-3 rounded-xl border transition text-lg text-left shadow hover:scale-[1.025]";

                  // Show green/red instantly on answer selection
                  if (isSelected) {
                    btnClass += feedback[current] === "correct"
                      ? " bg-green-500 border-green-600 text-white"
                      : " bg-red-500 border-red-700 text-white";
                  } else {
                    btnClass += " bg-slate-50 border-gray-200 hover:bg-blue-100";
                  }

                  return (
                    <button
                      key={idx}
                      onClick={() => selectAnswer(idx)}
                      disabled={timers[current] === 0}
                      className={btnClass}
                    >
                      <span className="font-bold mr-2">{String.fromCharCode(65 + idx)}.</span>
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex justify-between items-center mt-6 gap-2">
              <button
                className="px-4 py-2 rounded-xl bg-gray-200 hover:bg-gray-300 transition"
                onClick={prevQuestion}
                disabled={current === 0}
              >
                Previous
              </button>
              <button
                className="px-4 py-2 rounded-xl bg-gray-200 hover:bg-gray-300 transition"
                onClick={nextQuestion}
                disabled={current === questions.length - 1}
              >
                Next
              </button>
              <button
                className="px-6 py-2 rounded-xl bg-green-700 text-white font-semibold hover:bg-green-900 transition"
                onClick={submitQuiz}
              >
                Submit Quiz
              </button>
            </div>
            <div className="mt-2 text-gray-500 text-xs text-center">
              Select an answer to see immediate feedback. You can revisit and change any answer before submission.
            </div>
          </>
        )}

        {/* Summary Screen */}
        {step === "summary" && (
          <>
            <h2 className="text-xl font-bold mb-2 text-blue-700">Quiz Results</h2>
            <div className="mb-2">
              <span className="font-semibold">
                Correct Answers: {getResults().correct}
              </span>
              <br />
              <span className="font-semibold">
                Incorrect Answers: {getResults().incorrect}
              </span>
            </div>
            <div className="h-72 overflow-y-auto border rounded-xl p-2 bg-slate-50 mb-3 mt-2">
              {questions.map((q, idx) => (
                <div
                  key={q.id}
                  className="mb-3 p-2 rounded-xl shadow"
                  style={{
                    background:
                      answers[idx] === q.correctOption
                        ? "#d1fae5"
                        : answers[idx] == null
                        ? "#fef9c3"
                        : "#fee2e2"
                  }}
                >
                  <div className="font-semibold">
                    Q{idx + 1}: {q.question}
                  </div>
                  <div>
                    Your Answer:{" "}
                    {answers[idx] != null ? (
                      <>
                        <span
                          className={`${
                            answers[idx] === q.correctOption
                              ? "text-green-800 font-bold"
                              : "text-red-800"
                          }`}
                        >
                          {String.fromCharCode(65 + answers[idx])}.{" "}
                          {q.options[answers[idx]]}
                        </span>
                      </>
                    ) : (
                      <span className="text-yellow-800">Not Answered</span>
                    )}
                  </div>
                  <div>
                    Correct Answer:{" "}
                    <span className="text-green-800 font-bold">
                      {String.fromCharCode(65 + q.correctOption)}.{" "}
                      {q.options[q.correctOption]}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <button
              className="px-8 py-3 rounded-2xl bg-blue-700 text-white text-lg font-semibold hover:bg-blue-900 transition"
              onClick={restart}
            >
              Retry Quiz
            </button>
          </>
        )}
      </div>
      <div className="text-xs text-gray-400 mt-2">
        &copy; {new Date().getFullYear()} CA Analytical Quiz Demo – Curated by Deepak Bholusaria
      </div>
    </div>
  );
}

export default App;
